/**
 * Extract filename from a Content-Disposition header and download the response as a blob.
 * Extracted from the triplicated pattern in ai.service, dealNetwork.service, and admin.service.
 */
export async function downloadBlobWithFilename(
  response: Response
): Promise<{ blob: Blob; filename: string | null }> {
  const blob = await response.blob();

  const disposition =
    response.headers.get("content-disposition") ?? response.headers.get("Content-Disposition");

  let filename: string | null = null;
  if (disposition) {
    const filenameRegex = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i;
    const filenameMatch = filenameRegex.exec(disposition);
    if (filenameMatch?.[1]) {
      try {
        filename = decodeURIComponent(filenameMatch[1]);
      } catch {
        filename = filenameMatch[1];
      }
    }
  }

  return { blob, filename };
}

/**
 * Trigger a browser download for a blob with an optional filename.
 */
export function triggerBlobDownload(blob: Blob, filename?: string | null): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Open a blob in a new browser tab for inline preview (PDF, images).
 *
 * Callers reach this after awaiting a download, so the click's user activation is already
 * spent and Chrome blocks the new tab. An anchor click fails silently in that case — the
 * user clicks Preview and nothing at all happens. `window.open` returns null when blocked,
 * which is the only reliable signal available, so we use it and fall back to downloading
 * the file. A preview click always produces something.
 *
 * Returns how the blob was surfaced, so callers can adjust their messaging.
 */
export function triggerBlobPreview(
  blob: Blob,
  filename?: string | null,
  revokeDelayMs = 60_000
): "previewed" | "downloaded" {
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, "_blank");

  if (opened) {
    // Blob URLs are same-origin and created by us, but the opened tab has no business
    // reaching back into this one.
    try {
      opened.opener = null;
    } catch {
      // Cross-origin protections may already prevent this; nothing to do.
    }
    window.setTimeout(() => URL.revokeObjectURL(url), revokeDelayMs);
    return "previewed";
  }

  URL.revokeObjectURL(url);
  triggerBlobDownload(blob, filename);
  return "downloaded";
}
